import {Component, Input} from '@angular/core';
import {DecimalPipe} from '@angular/common';

/**
 * Read-only, fractional star rating bar.
 *
 * Renders an average like 4.3 honestly by overlaying a width-clipped row of
 * filled stars on top of an empty row — daisyUI's `rating` radios can only snap
 * to whole/half stars, so they are wrong for aggregate display. Use the daisyUI
 * `rating` component for interactive integer input instead.
 *
 * The reusable primitive behind every aggregate surface: list cards, the detail
 * header, and comment rows.
 */
@Component({
  selector: 'ui-star-bar',
  standalone: true,
  imports: [DecimalPipe],
  template: `
    <span class="inline-flex items-center gap-1.5 align-middle">
      <span class="relative inline-block leading-none select-none tracking-tight"
            [class.text-sm]="size === 'sm'"
            [class.text-lg]="size === 'lg'"
            [attr.aria-label]="ariaLabel"
            role="img">
        <span class="text-base-content/20" aria-hidden="true">★★★★★</span>
        <span class="absolute inset-0 overflow-hidden text-warning whitespace-nowrap"
              [style.width.%]="pct"
              aria-hidden="true">★★★★★</span>
      </span>
      @if (showValue && (count ?? 0) > 0) {
        <span class="text-sm text-base-content/60 whitespace-nowrap">
          {{ avg | number:'1.1-1' }}@if (count != null) { · {{ count }} }
        </span>
      }
    </span>
  `,
})
export class UiStarBarComponent {
  /** The average score (fractional), 0..5. */
  @Input() avg = 0;

  /** Number of ratings. When provided and > 0, shown next to the value. */
  @Input() count?: number;

  /** Show the numeric "avg · count" label after the stars. */
  @Input() showValue = true;

  /** Star glyph size. */
  @Input() size: 'sm' | 'md' | 'lg' = 'md';

  get pct(): number {
    const v = Number.isFinite(this.avg) ? this.avg : 0;
    return Math.max(0, Math.min(100, (v / 5) * 100));
  }

  get ariaLabel(): string {
    return `${(Number.isFinite(this.avg) ? this.avg : 0).toFixed(1)} out of 5`;
  }
}
